import { useState } from "react";
import { toast } from "sonner";
import { REGIME_LABELS, REGIMES_TRIBUTARIOS, type CustoDoMaterial, type RegimeTributario } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import type { ConfigFinanceira } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";

const MATERIAL: { valor: CustoDoMaterial; titulo: string; texto: string }[] = [
  { valor: "FICHA", titulo: "Pela ficha técnica", texto: "Conta o material que cada serviço concluído consumiu. As compras de material só mexem no caixa." },
  { valor: "COMPRAS", titulo: "Pelas compras", texto: "Conta as compras de material do mês, inclusive as de estoque. A ficha técnica não entra." },
];

const paraTexto = (bps: number | null) => (bps == null ? "" : (bps / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
function paraBps(texto: string): number | null {
  const limpo = texto.replace("%", "").trim();
  const n = Number(limpo.includes(",") ? limpo.replace(/\./g, "").replace(",", ".") : limpo);
  return texto.trim() && Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** Imposto sobre o faturamento e a regra do material: o que o DRE ligado precisa saber. */
export function ImpostosECustos({ config, pode }: { config: ConfigFinanceira; pode: boolean }) {
  const utils = trpc.useUtils();
  const [regime, setRegime] = useState<RegimeTributario | "">(config.regime ?? "");
  const [aliquota, setAliquota] = useState(paraTexto(config.aliquotaSobreFaturamentoBps));
  const [material, setMaterial] = useState<CustoDoMaterial>(config.custoDoMaterial);
  const [provisionar, setProvisionar] = useState(config.provisionarFolha);
  const [taxa, setTaxa] = useState(paraTexto(config.taxaDeProvisaoBps));
  const [aliquotaIr, setAliquotaIr] = useState(paraTexto(config.aliquotaSobreLucroBps));
  const bps = paraBps(aliquota);
  const invalida = !!aliquota.trim() && (bps == null || bps < 0 || bps > 3000);
  const taxaBps = paraBps(taxa);
  const taxaInvalida = provisionar && (taxaBps == null || taxaBps < 0 || taxaBps > 5000);
  const presumido = regime === "LUCRO_PRESUMIDO";
  const irBps = presumido ? paraBps(aliquotaIr) : null;
  const irInvalida = presumido && !!aliquotaIr.trim() && (irBps == null || irBps < 0 || irBps > 3000);
  const salvar = trpc.financeiro.configFinanceira.salvar.useMutation({
    onSuccess: () => {
      toast.success("Impostos e custos salvos.");
      utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const mudou =
    (regime || null) !== config.regime ||
    bps !== config.aliquotaSobreFaturamentoBps ||
    material !== config.custoDoMaterial ||
    provisionar !== config.provisionarFolha ||
    (provisionar && taxaBps !== config.taxaDeProvisaoBps) ||
    irBps !== config.aliquotaSobreLucroBps;

  return (
    <div className="space-y-4 border-t border-zinc-100 px-4 py-4 dark:border-border">
      <h4 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Impostos e custos</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={rotulo}>Regime tributário</span>
          <select value={regime} disabled={!pode} onChange={(e) => setRegime(e.target.value as RegimeTributario | "")} className={campo}>
            <option value="">Não informado</option>
            {REGIMES_TRIBUTARIOS.map((r) => (
              <option key={r} value={r}>
                {REGIME_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={rotulo}>Alíquota efetiva sobre o faturamento (%)</span>
          <input value={aliquota} disabled={!pode} inputMode="decimal" placeholder="Ex.: 6" onChange={(e) => setAliquota(e.target.value)} className={cn(campo, invalida && "border-red-400")} />
          <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">
            {invalida
              ? "Informe um número de 0 a 30."
              : presumido
                ? "PIS, COFINS e ISS somados, sobre o faturamento. Vale como estimativa até o imposto do mês ser lançado."
                : "Pergunte ao contador a alíquota efetiva do DAS. Vale como estimativa até o imposto do mês ser lançado."}
          </span>
        </label>
        {presumido && (
          <label className="block">
            <span className={rotulo}>IR e CSLL estimados sobre o faturamento (%)</span>
            <input value={aliquotaIr} disabled={!pode} inputMode="decimal" placeholder="Ex.: 7,68" onChange={(e) => setAliquotaIr(e.target.value)} className={cn(campo, irInvalida && "border-red-400")} />
            <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">
              {irInvalida ? "Informe um número de 0 a 30." : "Entra todo mês como estimativa; o DARF do trimestre lançado acerta a diferença no último mês dele."}
            </span>
          </label>
        )}
      </div>
      <fieldset>
        <legend className={rotulo}>Custo do material</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {MATERIAL.map((m) => (
            <button
              key={m.valor}
              type="button"
              disabled={!pode}
              aria-pressed={material === m.valor}
              onClick={() => setMaterial(m.valor)}
              className={cn(
                "rounded-lg border p-3 text-left transition-colors",
                material === m.valor ? "border-brand-600 bg-brand-50/60 dark:border-primary dark:bg-primary/10" : "border-zinc-200 hover:bg-zinc-50 dark:border-border dark:hover:bg-muted",
              )}
            >
              <span className="block text-sm font-semibold text-zinc-900 dark:text-foreground">{m.titulo}</span>
              <span className="mt-0.5 block text-xs text-zinc-500 dark:text-muted-foreground">{m.texto}</span>
            </button>
          ))}
        </div>
      </fieldset>
      <div className="rounded-lg border border-zinc-200 p-3 dark:border-border">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={provisionar} disabled={!pode} onChange={(e) => setProvisionar(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-600" />
          <span>
            <span className="block text-sm font-semibold text-zinc-900 dark:text-foreground">Provisionar 13º e férias</span>
            <span className="block text-xs text-zinc-500 dark:text-muted-foreground">
              Todo mês entra em Pessoal uma parte dos salários da folha, e o 13º e as férias pagos só mexem no caixa. Assim dezembro não parece um desastre.
            </span>
          </span>
        </label>
        {provisionar && (
          <label className="mt-2 block sm:w-1/2">
            <span className={rotulo}>Taxa sobre os salários (%)</span>
            <input value={taxa} disabled={!pode} inputMode="decimal" onChange={(e) => setTaxa(e.target.value)} className={cn(campo, taxaInvalida && "border-red-400")} />
            <span className="mt-1 block text-[11px] text-zinc-500 dark:text-muted-foreground">
              {taxaInvalida ? "Informe um número de 0 a 50." : "21% = 1/12 de 13º + 1/12 de férias com o terço + FGTS. Ajuste com o contador se houver outros encargos."}
            </span>
          </label>
        )}
      </div>
      {pode && (
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!mudou || invalida || taxaInvalida || irInvalida || salvar.isPending}
            onClick={() =>
              salvar.mutate({
                dreLigado: true,
                regime: regime || null,
                aliquotaSobreFaturamentoBps: bps,
                custoDoMaterial: material,
                provisionarFolha: provisionar,
                ...(provisionar && taxaBps != null ? { taxaDeProvisaoBps: taxaBps } : {}),
                aliquotaSobreLucroBps: irBps,
              })
            }
            className="h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}
